import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  FormControl,
  Divider,
  Paper
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { makeRequest } from "../../../assets/functions_helper";
import { CustomTextField, CustomSelect, CustomInputLabel, CustomMenuItem } from "../../../components/textFields";
import { SendButton, CloseButton } from "../../../components/buttons";

export default function CreateSurveyDialog({ restaurantId, onSuccess, onClose, showSnackbar }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [questions, setQuestions] = useState([
    { type: "numeric_rating", text: "", display_type: "stars" }
  ]);
  const [loading, setLoading] = useState(false);

  const handleAddQuestion = () => {
    if (questions.length < 5) {
      setQuestions([
        ...questions,
        { type: "numeric_rating", text: "", display_type: "stars" }
      ]);
    } else {
      showSnackbar("No puedes añadir más de 5 preguntas", "warning");
    }
  };

  const handleRemoveQuestion = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const handleQuestionChange = (index, field, value) => {
    const newQuestions = [...questions];
    newQuestions[index][field] = value;
    setQuestions(newQuestions);
  };

  const handleSubmit = () => {
    // Validaciones basicas
    if (!name.trim()) {
      showSnackbar("El nombre de la encuesta es obligatorio", "error");
      return;
    }
    if (!description.trim()) {
      showSnackbar("La descripción de la encuesta es obligatoria", "error");
      return;
    }

    if (questions.length === 0) {
      showSnackbar("Debes incluir al menos una pregunta", "error");
      return;
    }

    // Validar preguntas
    for (let i = 0; i < questions.length; i++) {
      if (!questions[i].text.trim()) {
        showSnackbar(`La pregunta ${i + 1} no tiene texto.`, "error");
        return;
      }
    }

    setLoading(true);

    const payload = {
      restaurant_id: restaurantId,
      name,
      description,
      questions: questions.map((q, index) => ({
        question_id: `q${Date.now()}_${index}`,
        type: q.type,
        text: q.text,
        display_type: q.type === "numeric_rating" ? q.display_type : undefined,
      }))
    };

    makeRequest(
      "POST",
      "surveys/create",
      payload,
      "application/json",
      localStorage.getItem("token"),
      localStorage.getItem("tokenType")
    )
      .then((res) => {
        showSnackbar("Encuesta creada con éxito", "success");
        onSuccess(res);
      })
      .catch((err) => {
        showSnackbar("Error al crear la encuesta", "error");
        console.error(err);
      })
      .finally(() => setLoading(false));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Completa este formulario para habilitar una nueva encuesta a tus clientes. Recuerda que puedes tener hasta 5 preguntas como máximo.
      </Typography>

      <CustomTextField
        label="Nombre de la Encuesta"
        value={name}
        onChange={(e) => setName(e.target.value)}
        fullWidth
        size="small"
        placeholder="Ej: Satisfacción Q1 2025"
      />

      <CustomTextField
        label="Descripción (Call to action)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        multiline
        rows={2}
        fullWidth
        size="small"
        placeholder="Ej: Ayúdanos a mejorar tu próxima visita."
      />

      <Divider sx={{ my: 1 }} />

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Preguntas ({questions.length}/5)
        </Typography>
        <SendButton
          startIcon={<AddCircleIcon />}
          onClick={handleAddQuestion}
          disabled={questions.length >= 5 || loading}
          size="small"
          sx={{ padding: "5px 10px", fontSize: "0.85rem", borderRadius: "8px" }}
        >
          Añadir Pregunta
        </SendButton>
      </Box>

      {questions.map((q, idx) => (
        <Paper
          key={idx}
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 2,
            position: "relative",
            borderColor: "#e0e0e0",
            backgroundColor: "#fcfcfc"
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="subtitle2" color="primary">
              Pregunta {idx + 1}
            </Typography>
            <IconButton
              size="small"
              color="error"
              onClick={() => handleRemoveQuestion(idx)}
              disabled={questions.length <= 1 || loading}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>

          <CustomTextField
            label="Texto de la pregunta"
            value={q.text}
            onChange={(e) => handleQuestionChange(idx, "text", e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            placeholder="¿Qué tal estuvo tu comida?"
          />

          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl fullWidth size="small">
              <CustomInputLabel>Tipo de Respuesta</CustomInputLabel>
              <CustomSelect
                value={q.type}
                label="Tipo de Respuesta"
                onChange={(e) => handleQuestionChange(idx, "type", e.target.value)}
              >
                <CustomMenuItem value="numeric_rating">Valoración (Rating)</CustomMenuItem>
                <CustomMenuItem value="open_ended">Respuesta Abierta</CustomMenuItem>
              </CustomSelect>
            </FormControl>

            {q.type === "numeric_rating" && (
              <FormControl fullWidth size="small">
                <CustomInputLabel>Mostrar como</CustomInputLabel>
                <CustomSelect
                  value={q.display_type}
                  label="Mostrar como"
                  onChange={(e) => handleQuestionChange(idx, "display_type", e.target.value)}
                >
                  <CustomMenuItem value="stars">Estrellas</CustomMenuItem>
                  <CustomMenuItem value="numeric">Números (1-5)</CustomMenuItem>
                </CustomSelect>
              </FormControl>
            )}
          </Box>
        </Paper>
      ))}

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 2 }}>
        <CloseButton onClick={onClose} disabled={loading} color="inherit" sx={{ textTransform: "none" }}>
          Cancelar
        </CloseButton>
        <SendButton onClick={handleSubmit} disabled={loading} variant="contained" sx={{ textTransform: "none", borderRadius: 2 }}>
          {loading ? "Guardando..." : "Finalizar y Crear"}
        </SendButton>
      </Box>
    </Box>
  );
}
